/**
 * Terms of service acceptance state of the signed-in user (backend
 * middleware/requireTermsAcceptance.ts).
 *
 *  - `readOnly`: the account has not accepted the terms in force, so the API
 *    refuses writes with 403 TERMS_ACCEPTANCE_REQUIRED. Set by
 *    TermsOfServiceConsent (from GET /api/v1/me/legal-status) and by apiClient
 *    (from such a 403); TermsReadOnlyNotice (rendered by AppLayout on every
 *    page) explains it. Cleared after the acceptance and on sign-out.
 *  - `dialogRequests`: a counter TermsOfServiceConsent watches — every
 *    requestTermsDialog() (the notice's "Review and accept" button) opens the
 *    dialog again.
 */
type Listener = () => void;

export const TERMS_ACCEPTANCE_REQUIRED_CODE = 'TERMS_ACCEPTANCE_REQUIRED';

/** Settings page with the data export and the account deletion (pages/SettingsPage.tsx). */
export const ACCOUNT_SETTINGS_PATH = '/settings';

export interface TermsState {
  readOnly: boolean;
  dialogRequests: number;
}

let state: TermsState = { readOnly: false, dialogRequests: 0 };
const listeners = new Set<Listener>();

function set(next: TermsState): void {
  if (next.readOnly === state.readOnly && next.dialogRequests === state.dialogRequests) return;
  state = next;
  listeners.forEach((listener) => listener());
}

export function getTermsState(): TermsState {
  return state;
}

export function subscribeTermsState(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function markTermsAcceptanceRequired(): void {
  set({ ...state, readOnly: true });
}

/** The terms were accepted, or the user signed out (the next user has their own state). */
export function clearTermsAcceptanceRequired(): void {
  set({ ...state, readOnly: false });
}

/** Open the terms dialog (again). */
export function requestTermsDialog(): void {
  set({ ...state, dialogRequests: state.dialogRequests + 1 });
}

/** Test-only: forget everything. */
export function resetTermsStateForTests(): void {
  state = { readOnly: false, dialogRequests: 0 };
  listeners.forEach((listener) => listener());
}
